import { css, ThemeProvider as ThemeProvider$1 } from 'styled-components';
import { jsx } from 'react/jsx-runtime';
import { useEffect } from 'react';
import { S as ScreenSize, i as getBreakpoints, l as getTypography, B as BASE, n as getSpacing } from './hideScrollbar-9514ab2a.js';

const getRgbString = (rv, gv, bv) => {
  const r = parseInt(rv, 16);
  const g = parseInt(gv, 16);
  const b = parseInt(bv, 16);
  return `${r},${g},${b}`;
};
const hexToRgb = hex => {
  /**
   * The bundler has issues using `slice()` and appends `.withConfig`
   * to it ("slice.withConfig"). This is something to do with styled-components, however
   * abstracting this logic out and switching to split stops the TypeError that was occuring
   * in the legacy bundle.... 🤷‍♂️ FML
   */
  const a = hex.split('');
  return getRgbString(`${a[1]}${a[2]}`, `${a[3]}${a[4]}`, `${a[5]}${a[6]}`);
};
const isRGBColor = c => typeof c === 'string' && /[0-9]+,[0-9]+,[0-9]+/.test(c);
const isRGBAColor = c => typeof c === 'function';
/**
 * Takes Color type values and transforms them if necessary into valid CSS.
 * Can be used for any color property in CSS (color, background-color etc).
 */
const applyColor = function (color) {
  let opacity = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 1;
  if (isRGBAColor(color)) {
    return color(opacity);
  }
  if (isRGBColor(color)) {
    return `rgb(${color})`;
  }
  return color;
};

function lighten(rgb, percent) {
  const arr = rgb.split(',');
  const r = +arr[0];
  const g = +arr[1];
  const b = +arr[2];
  return [Math.round(r + r * percent), Math.round(g + g * percent), Math.round(b + b * percent)].join();
}

const Ellipsis = () => css`
  width: 100%;
  padding-right: 10px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const EllipsisWithLineClamp = function () {
  let lineClamp = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 1;
  return css`
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${lineClamp};
`;
};

const system = {
  //  Brand
  brand01: '#2394DF',
  brand02: '#43C1EE',
  brand03: '#356EE9',
  brand04: '#02ABDE',
  //  Charts
  chart01: '#2394DF',
  chart02: '#71E7D6',
  chart03: '#BB4786',
  chart04: '#E5B061',
  chart05: '#A54CEC',
  chart06: '#E75D75',
  //  Signals
  bad: '#E64141',
  good: '#2DC97E',
  neutral: '#EEB219',
  warning: '#FF9212',
  //  Social
  google: '#D1493C',
  facebook: '#4868AD',
  //  Structural
  black: '#000000',
  almostBlack: '#0B0E13',
  charcoal: '#151B24',
  noir: '#1B222D',
  phantom: '#202833',
  spaceGray: '#262E3A',
  darkerGray: '#3E4855',
  darkGray: '#5F6875',
  mediumGray: '#8C929B',
  lightGray: '#C9CBCF',
  paper: '#F2F2F3',
  paleWhite: '#EAF4FF',
  white: '#FFFFFF',
  // TODO: remove these when snowflake rings change to system colours
  snowflakeRingEven: '#2D3642',
  showflakeRingOdd: '#424B58'
};
const systemLight = {
  black: '#FFFFFF',
  charcoal: '#FFFFFF',
  noir: '#FFFFFF',
  phantom: '#FFFFFF',
  spaceGray: '#FFFFFF',
  white: '#262E3A',
  paleWhite: '#8C929B'
};
const getColor = appTheme => {
  const systemColour = appTheme === 'dark' ? system : Object.assign({}, system, systemLight);
  return Object.assign({}, Object.entries(systemColour).reduce((a, _ref) => {
    let [name, hex] = _ref;
    const rgb = hexToRgb(hex);
    return Object.assign({}, a, {
      [name]: hex,
      [`${name}RGB`]: rgb,
      [`${name}RGBA`]: alpha => `rgba(${rgb},${alpha >= 0 && alpha <= 1 ? alpha : 1})`
    });
  }, {}));
};

const getBarChart = color => ({
  assetsVsLiabilities: {
    colors: [color.chart01, color.chart02],
    titleColors: [color.white, color.spaceGray]
  },
  pastGrowth: {
    colors: [color.chart01, color.chart02, color.chart03],
    titleColors: [color.white, color.spaceGray, color.white]
  },
  futureGrowth: {
    colors: [color.chart01, color.chart02, color.chart03],
    titleColors: [color.white, color.spaceGray, color.white]
  },
  valuator: {
    colors: [color.brand01, color.chart02, color.chart03, color.chart05],
    valueColors: [color.brand01, color.white, color.white, color.white]
  },
  dividendYieldVsMarket: {
    colors: [color.chart01, color.chart03RGBA(0.5), color.chart03, color.chart02, color.chart01],
    titleColors: [color.white, color.white, color.white, color.spaceGray, color.white]
  },
  futureAnnualGrowthAnalysis: {
    colors: [color.chart01, color.chart02, color.chart03],
    titleColors: [color.white, color.spaceGray, color.white]
  },
  incomeStatement: {
    colors: [color.chart01, color.badRGBA(0.5), color.good, color.badRGBA(0.5), color.chart02],
    titleColors: [color.white, color.whiteRGBA(0.3), color.white, color.whiteRGBA(0.3), color.white]
  }
});

const theme = function () {
  var _config$screenSize, _config$typographyBas;
  let config = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const spacing = getSpacing();
  let appTheme = config && config.screenSize === ScreenSize.Print ? 'light' : 'dark';
  if (typeof (config === null || config === void 0 ? void 0 : config.theme) !== 'undefined') {
    appTheme = config.theme;
  }
  const color = getColor(appTheme);
  const barChart = getBarChart(color);
  const screenSize = (_config$screenSize = config.screenSize) !== null && _config$screenSize !== void 0 ? _config$screenSize : ScreenSize.Default;
  const breakpoints = getBreakpoints(screenSize);
  const typographyBaseline = (_config$typographyBas = config.typographyBaselineMeasurementUnit) !== null && _config$typographyBas !== void 0 ? _config$typographyBas : 'rem';
  const typography = getTypography(typographyBaseline);
  let safeAreaInsetTop = '0px';
  try {
    if (CSS.supports('padding-top', 'env(safe-area-inset-top)')) {
      safeAreaInsetTop = 'env(safe-area-inset-top)';
    }
  } catch {
    /** silent */
  }
  let safeAreaInsetBottom = '0px';
  try {
    if (CSS.supports('padding-bottom', 'env(safe-area-inset-bottom)')) {
      safeAreaInsetBottom = 'env(safe-area-inset-bottom)';
    }
  } catch {
    /** silent */
  }
  return Object.assign({
    /* Safe Areas */
    safeAreaInsetTop,
    safeAreaInsetBottom,
    /* Font */
    fontBase: BASE,
    /* Spacing */
    flakeMinColour: '#FF2B2B',
    flakeMaxColour: '#00e613'
  }, spacing, breakpoints, {
    appTheme,
    /* Colours */
    color,
    /* Typography */
    typography,
    /* Charts */
    chart: {
      barChart
    },
    /* Focus */
    focus: type => ({
      'data-focus': type
    }),
    desktopTopbarHeight: spacing.x8spacePx,
    mobileTopbarHeight: 114,
    sidebarWidth: 298
  });
};

/**
 * Applies and removes 'using-mouse' class to the document body.
 * This allows components to apply styling depending on whether
 * user is using a keyboard or mouse - for example focus styles.
 */
const useUsingMouse = () => {
  useEffect(() => {
    const onMousedown = () => {
      document.body.classList.add('using-mouse');
    };
    const onKeydown = e => {
      switch (e.key) {
        case 'Tab':
          document.body.classList.remove('using-mouse');
          break;
      }
    };
    document.body.addEventListener('mousedown', onMousedown);
    document.body.addEventListener('keydown', onKeydown);
    return () => {
      document.body.removeEventListener('mousedown', onMousedown);
      document.body.removeEventListener('keydown', onKeydown);
    };
  }, []);
};
const ThemeProvider = _ref => {
  let {
    theme: theme$1,
    children
  } = _ref;
  useUsingMouse();
  return jsx(ThemeProvider$1, {
    theme: theme$1 ? theme$1 : theme(),
    children: children
  });
};

export { Ellipsis as E, ThemeProvider as T, applyColor as a, EllipsisWithLineClamp as b, lighten as l, theme as t };
